import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTASection } from "@/components/site/CTASection";
import { Reveal } from "@/components/motion/Reveal";
import { getIcon } from "@/lib/icons";

/** Generic hub page: editorial card grid. Uses a fine-line icon when no photo exists. */
export const HubPage = ({ ns, itemsKey, base, imageMap, heroImage, testId }) => {
  const { t } = useTranslation();
  const items = t(itemsKey, { returnObjects: true }) || [];

  return (
    <div data-testid={testId}>
      <PageHero
        kicker={t(`${ns}.hubKicker`, "")}
        title={t(`${ns}.hubTitle`)}
        text={t(`${ns}.hubText`, "")}
        image={heroImage}
      />
      <section className="bg-warm-ivory">
        <div className="mx-auto max-w-7xl px-5 pb-24 lg:px-10 lg:pb-28">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((it, i) => {
              const hasImage = imageMap && imageMap[it.slug];
              const Icon = getIcon(it.slug);
              return (
                <Reveal key={it.slug} delay={(i % 3) * 0.06}>
                  <Link
                    to={`${base}/${it.slug}`}
                    data-testid={`hub-card-${it.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-transparent bg-sand transition-colors duration-500 hover:border-champagne-gold/45"
                  >
                    {hasImage && (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={imageMap[it.slug]}
                          alt=""
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col p-7">
                      {!hasImage && (
                        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-warm-ivory text-champagne-gold ring-1 ring-champagne-gold/25 transition-colors duration-500 group-hover:bg-champagne-gold/10">
                          <Icon strokeWidth={1} className="h-7 w-7" />
                        </div>
                      )}
                      {it.category && (
                        <span className="mb-2 text-xs font-medium uppercase tracking-editorial text-muted-sage">
                          {it.category}
                        </span>
                      )}
                      <h3 className="font-serif text-2xl leading-snug text-deep-ink transition-colors duration-500 group-hover:text-champagne-gold">
                        {it.title}
                      </h3>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-deep-ink/70 line-clamp-4">
                        {it.summary || it.excerpt}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-champagne-gold">
                        {t("common.learnMore")}
                        <ArrowRight className="rtl-flip h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
};
